import { useCallback, useMemo } from 'react';
import { useDocumentContext } from './context';
import { ACTIONS } from './reducer';
import { useKeycloak } from '@react-keycloak/web';
import { API_ENDPOINTS } from '../apiEndpoints';
import { BASE_URL } from 'utils/axios';

export const useDocument = () => {
    const [state, dispatch] = useDocumentContext();
    const { keycloak } = useKeycloak();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const setCurrentLoadingDoc = useCallback(
        (docId) => {
            dispatch({ type: ACTIONS.SET_CURRENT_LOADING_DOC, payload: docId });
        },
        [dispatch]
    );

    const fetchDocuments = useCallback(
        async (documentId) => {
            if (!documentId || !keycloak?.token) return;

            try {
                setLoading(true);
                const response = await fetch(`${BASE_URL}/${API_ENDPOINTS.DOCUMENTS}/${documentId}?page=0&size=100`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${keycloak.token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status}`);
                }

                const data = await response.json();
                dispatch({
                    type: ACTIONS.SET_DOCUMENTS,
                    payload: data.content || []
                });
                // eslint-disable-next-line consistent-return
                return data;
            } catch (error) {
                console.error('Error fetching documents:', error);
                dispatch({ type: ACTIONS.SET_DOCUMENTS, payload: [] });
            } finally {
                setLoading(false);
            }
        },
        [keycloak?.token, dispatch, setLoading]
    );

    const downloadDocument = useCallback(
        async (documentId, fileName) => {
            try {
                setCurrentLoadingDoc(documentId);
                setLoading(true);

                const response = await fetch(`${API_ENDPOINTS.DOCUMENTS}/${documentId}`, {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Download failed: ${response.status}`);
                }

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.download = fileName || 'document';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading document:', error);
            } finally {
                setLoading(false);
                setCurrentLoadingDoc(null);
            }
        },
        [keycloak?.token, setLoading, setCurrentLoadingDoc]
    );

    const previewDocument = useCallback(
        async (documentId) => {
            try {
                setCurrentLoadingDoc(documentId);
                setLoading(true);

                const response = await fetch(`${API_ENDPOINTS.DOCUMENTS}/${documentId}`, {
                    headers: {
                        Authorization: `Bearer ${keycloak.token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Preview failed: ${response.status}`);
                }

                const blob = await response.blob();
                return URL.createObjectURL(blob);
            } catch (error) {
                console.error('Error previewing document:', error);
                throw error;
            } finally {
                setLoading(false);
                setCurrentLoadingDoc(null);
            }
        },
        [keycloak?.token, setLoading, setCurrentLoadingDoc]
    );

    return useMemo(
        () => ({
            // State
            documents: state.documents,
            loading: state.loading,
            currentLoadingDoc: state.currentLoadingDoc,

            // Actions
            fetchDocuments,
            downloadDocument,
            previewDocument
        }),
        [state, fetchDocuments, downloadDocument, previewDocument]
    );
};

export default useDocument;
